import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  LinearProgress,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import ListItem from '@mui/material/ListItem';
import { useQuery } from '@tanstack/react-query';
import { Auth } from 'aws-amplify';
import axios from 'axios';
import React from 'react';
import IConnectLogo from '../../assets/integrity-connect-logo.png';
import { captureError } from '../../utils/capture-error';
import { navRowSx } from './nav-row';

export function IConnectSignIn(props: { link: string | undefined }) {
  // Props
  const { link } = props;
  // State
  const [open, setOpen] = React.useState(false);

  const query = useQuery({
    queryKey: ['integrity-connect-login'],
    enabled: open,
    queryFn: async () => {
      const path = '/business/integrity-connect/login';
      const Authorization = (await Auth.currentSession())
        .getAccessToken()
        .getJwtToken();
      const url = process.env.REACT_APP_DASHBOARD_API_URL + path;
      const response = await axios.post(
        url,
        {},
        { headers: { Authorization } },
      );

      return response.data;
    },
    onSuccess: (formHtml) => {
      const parsed = new DOMParser().parseFromString(formHtml, 'text/html');
      const form = parsed.querySelector('#iconnect_saml');

      if (!form) return;

      const liveForm = document.importNode(form, true) as HTMLFormElement;
      liveForm.target = 'iconnect_popup';

      document.body.appendChild(liveForm);
      liveForm.submit();
      liveForm.remove();

      setOpen(false);
    },
    onError: (error) => captureError({ data: { error } }),
  });

  return (
    <React.Fragment>
      <ListItem
        disablePadding
        onClick={() => {
          setOpen(true);
        }}
      >
        <ListItemButton sx={navRowSx({ depth: 2 })}>
          <ListItemIcon sx={{ minWidth: 32 }}></ListItemIcon>
          <ListItemText primary="Sign In" disableTypography />
        </ListItemButton>
      </ListItem>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle
          sx={{
            mb: 2,
            borderRadius: 1,
            display: 'flex',
            backgroundColor: 'primary.main',
          }}
        >
          <Box
            maxWidth={100}
            component="img"
            src={IConnectLogo}
            sx={{ mx: 'auto' }}
          />
        </DialogTitle>
        <DialogContent
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <DialogContentText sx={{ py: 3 }}>
            {query.isFetching ? (
              <Box sx={{ fontSize: 18 }}>
                Attempting to sign in to IntegrityCONNECT...
              </Box>
            ) : query.isError ? (
              <Alert severity="error">
                We were unable to authorize you with Integrity Connect.
                <Button
                  size="small"
                  color="primary"
                  href={link || ''}
                  target="_blank"
                  sx={{
                    textTransform: 'none',
                    fontFamily: 'Roboto',
                    fontWeight: 700,
                  }}
                >
                  Click here to sign up/in
                </Button>
              </Alert>
            ) : null}
          </DialogContentText>
        </DialogContent>
        {query.isFetching && <LinearProgress color="primary" />}
        <DialogActions>
          <Button color="primary" onClick={() => setOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
}
